"use client";

import React, { Children, useEffect, useMemo, type ReactNode } from "react";

import styles from "@/components/common/ResultsSliderBase/Results.module.scss";
import {
  ResultsNextArrow,
  ResultsPrevArrow,
} from "@/components/common/ResultsSliderBase/ResultsArrows";
import { useResultsContext } from "@/components/common/ResultsSliderBase/ResultsContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import Slider, { type Settings as SlickSettings } from "@/lib/react-slick";
import { cn } from "@/utils/general";

type ThumbnailSliderProps = {
  children?: ReactNode;
  className?: string;
  settings?: SlickSettings;
  theme?: "nova" | "wild";
  stretch?: boolean;
};

export function ResultsThumbnailSlider({
  children,
  className,
  settings,
  theme = "nova",
  stretch = false,
}: ThumbnailSliderProps) {
  const { autoplay, resultSliderRef, setThumbnailSliderRef, slidesCount } = useResultsContext();

  /*
   * Remount slick when the viewport class changes so it re-measures instead of keeping stale sizing
   */
  const isMobile = useIsMobile(769);
  const isBelowDesktop = useIsMobile(1280);

  // Automatically assign indices to Thumbnail children
  const childrenWithIndices = useMemo(() => {
    return Children.map(children, (child, index) => {
      if (React.isValidElement(child)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const childType = child.type as any;
        if (childType?.displayName === "Results.Thumbnail") {
          return React.cloneElement(child, { index } as { index: number });
        }
      }
      return child;
    });
  }, [children]);

  useEffect(() => {
    if (!autoplay && resultSliderRef?.slickPause) {
      resultSliderRef.slickPause();
    }
  }, [autoplay, resultSliderRef]);

  const sliderSettings: SlickSettings & { ref?: (slider: Slider | null) => void } = {
    /* One-way sync from the main slider via slickGoTo; see ResultsSlider. */
    swipe: false,
    draggable: false,
    ref: (slider: Slider | null) => {
      setThumbnailSliderRef(slider);
    },
    speed: 500,
    swipeToSlide: false,
    adaptiveHeight: false,
    centerMode: false,
    dots: false,
    arrows: false,
    infinite: false,
    slidesToShow: slidesCount,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          infinite: slidesCount > 3,
          centerMode: slidesCount > 3,
          slidesToShow: 3,
          centerPadding: "0px",
        },
      },
    ],
    ...settings,
  };

  let key: "mobile" | "tablet" | "desktop" = "desktop";
  if (isMobile) {
    key = "mobile";
  } else if (isBelowDesktop) {
    key = "tablet";
  }

  return (
    <div className={cn("flex justify-between md:justify-center items-center w-full", className)}>
      <div className="mr-2 md:mr-6">
        <ResultsPrevArrow theme={theme} variant="small" />
      </div>
      <div
        className={cn(
          styles.thumbnailSliderWrapper,
          "relative w-[192px] smmd:w-[327px] md:w-auto",
          stretch && "md:!w-[80%] stretch"
        )}
      >
        <Slider key={key} {...sliderSettings}>
          {childrenWithIndices}
        </Slider>
      </div>
      <div className="ml-2 md:ml-6">
        <ResultsNextArrow theme={theme} variant="small" />
      </div>
    </div>
  );
}
